import type { Blogger } from "@/shared/api/wb";
import { normalizePlatform } from "@/features/blogger-card";
import type { PlatformKey } from "@/features/blogger-card";

export type { PlatformKey };

export function mh(s: string): number {
  let h = 0;
  for (const c of s) h = (h * 31 + c.charCodeAt(0)) & 0x0fffffff;
  return h;
}

export type AgeGroup = { label: string; male: number; female: number };

export type PlatformProfile = {
  key: PlatformKey;
  label: string;
  avgViews: number;
  er: number;
  cpv: number;
  liveAudience: number;
  postsPerMonth: number;
  viewsDeltaPct: number;
  erVsMedian: number;
  livePercentile: number;
  malePct: number;
  ageGroups: AgeGroup[];
  geo: { city: string; pct: number }[];
  interests: { label: string; pct: number; relevant?: boolean }[];
  sparkline: number[];
  formatReach: { format: string; range: string }[];
  adsPerMonth: number;
  reach24h: number; reach48h: number; reach72h: number;
  liveAudiencePct: number;
  suspiciousPct: number;
  botsPct: number;
  organicGrowth: "ok" | "warn" | "bad";
  massFollowing: "ok" | "warn" | "bad";
  viewsManipulation: "ok" | "warn" | "bad";
  reactionsManipulation: "ok" | "warn" | "bad";
};

export type PostData = {
  id: string; type: "photo" | "text" | "video";
  excerpt: string; views: number; date: string;
};

export type HistoryEntry = {
  brand: string; formatLabel: string; date: string;
  result: "great" | "ok"; deltaLabel: string;
};

export type ReviewEntry = {
  id: string; author: string; company: string;
  date: string; stars: number; text: string;
  format: string; segment: string;
};

export type SimilarBlogger = {
  id: string; name: string; platform: PlatformKey;
  category: string; cpv: number; er: number;
};

export type BloggerProfileData = {
  platforms: PlatformKey[];
  qualityScore: number;
  geoLabel: string;
  yearsOnPlatform: number;
  responseHours: number;
  isVerified: boolean;
  completedCampaigns: number;
  platformStats: Partial<Record<PlatformKey, PlatformProfile>>;
  posts: PostData[];
  history: HistoryEntry[];
  reviews: ReviewEntry[];
  similar: SimilarBlogger[];
};

const PLATFORM_LABELS: Record<PlatformKey, string> = {
  tg: "Telegram", vk: "ВКонтакте", yt: "YouTube",
  dzen: "Дзен", tt: "TikTok", lk: "Лайки", max: "МАХ",
};

const GEOS = ["Москва", "Санкт-Петербург", "Новосибирск", "Екатеринбург", "Казань", "Краснодар"];
const BRANDS = ["Skyeng", "Сбер Маркет", "Lamoda", "Яндекс", "Тинькофф", "МТС", "VK Pay", "Ozon", "Авито", "Wildberries"];
const AUTHORS = [
  { name: "Андрей К.", company: "Финтех Старт" },
  { name: "Ольга С.", company: "E-commerce Pro" },
  { name: "Дмитрий Р.", company: "DigitalAgency" },
  { name: "Мария Л.", company: "RetailGroup" },
  { name: "Иван П.", company: "SaaS Platform" },
];
const REVIEW_TEXTS = [
  "Всё чётко по срокам, охваты выше прогноза. Будем работать снова — блогер профессиональный, держит тему.",
  "Отличный опыт сотрудничества. Аудитория отреагировала живо, много органических переходов на сайт.",
  "Сработались быстро. Блогер предложил интересную подачу материала, не как обычная реклама.",
  "Немного затянулся согласовательный процесс, но результат хороший. CPV вышел ниже планового на 12%.",
  "Рекомендую. Аудитория вовлечённая, комментарии реальные. Второй раз работаем с этим блогером.",
];
const POST_EXCERPTS = [
  "Попробовал новый сервис — реально удобно, особенно для тех кто…",
  "Разбираем тему инвестиций на пальцах: куда вложить в 2025-м и что точно не стоит…",
  "Партнёрский пост, но расскажу честно: пользуюсь сам уже 3 месяца и вот что…",
  "Много спрашивали про это — отвечаю подробно, со своим опытом использования…",
  "Краткий разбор: плюсы, минусы, для кого подойдёт, для кого нет…",
  "Один из тех случаев, когда реклама оказалась полезной: делюсь находкой…",
];

function makePlatformProfile(
  key: PlatformKey, id: string,
  baseViews: number, baseEr: number, baseCpv: number, viewScale = 1,
): PlatformProfile {
  const h = (s: string) => mh(id + s);
  const isTg = key === "tg";
  const isVk = key === "vk";

  const avgViews = Math.round(baseViews * viewScale * (0.85 + (h("v") % 30) / 100));
  const er = +(baseEr * viewScale * (0.8 + (h("er") % 40) / 100)).toFixed(1);
  const cpv = +(baseCpv / viewScale * (0.9 + (h("cpv") % 20) / 100)).toFixed(2);
  const liveAudience = 80 + (h("la") % 18);
  const postsPerMonth = (isTg ? 28 : isVk ? 18 : 12) + (h("pm") % 12);
  const viewsDeltaPct = -5 + (h("vd") % 20);
  const erVsMedian = 1.1 + (h("em") % 10) / 10;
  const livePercentile = 60 + (h("lp") % 36);

  const malePct = isTg
    ? 55 + (h("mp") % 20)
    : isVk ? 38 + (h("mp") % 24) : 45 + (h("mp") % 20);

  const ageGroups: AgeGroup[] = [
    { label: "13–17", male: 2 + (h("a1m") % 4),  female: 3 + (h("a1f") % 5) },
    { label: "18–24", male: 12 + (h("a2m") % 12), female: 14 + (h("a2f") % 12) },
    { label: "25–34", male: 28 + (h("a3m") % 14), female: 22 + (h("a3f") % 14) },
    { label: "35–44", male: 18 + (h("a4m") % 10), female: 16 + (h("a4f") % 10) },
    { label: "45+",   male: 8  + (h("a5m") % 8),  female: 10 + (h("a5f") % 8) },
  ];

  const cities = [
    { city: "Москва",         pct: 32 + (h("g0") % 12) },
    { city: "Санкт-Петербург",pct: 14 + (h("g1") % 8) },
    { city: "Новосибирск",    pct: 5  + (h("g2") % 6) },
    { city: "Екатеринбург",   pct: 4  + (h("g3") % 5) },
  ];
  const otherPct = Math.max(0, 100 - cities.reduce((s, c) => s + c.pct, 0));
  const geo = [...cities, { city: "Регионы РФ", pct: otherPct }];

  const interestPool = isTg
    ? ["Финансы", "IT", "Новости", "Бизнес", "Криптовалюты", "Саморазвитие", "Маркетинг"]
    : isVk
    ? ["Развлечения", "Мода", "Кулинария", "Путешествия", "Спорт", "Красота", "Технологии"]
    : ["Развлечения", "Путешествия", "Спорт", "Музыка", "Кино", "Лайфстайл", "Технологии"];
  const interests = interestPool.map((label, i) => ({
    label, pct: 15 + (h("i" + i) % 45), relevant: i < 3,
  })).sort((a, b) => b.pct - a.pct).slice(0, 6);

  const baseBar = avgViews * 0.7;
  const sparkline = Array.from({ length: 12 }, (_, i) =>
    Math.round(baseBar + (h("sp" + i) % (avgViews * 0.6))));

  const formatReach = isTg ? [
    { format: "Посев 1/24", range: `${Math.round(avgViews * 0.8 / 1000)}К–${Math.round(avgViews * 1.1 / 1000)}К` },
    { format: "Посев 2/48", range: `${Math.round(avgViews * 1.2 / 1000)}К–${Math.round(avgViews * 1.5 / 1000)}К` },
    { format: "Посев 3/72", range: `${Math.round(avgViews * 1.5 / 1000)}К–${Math.round(avgViews * 1.9 / 1000)}К` },
    { format: "Нативный пост", range: `${Math.round(avgViews * 0.9 / 1000)}К–${Math.round(avgViews * 1.3 / 1000)}К` },
  ] : isVk ? [
    { format: "Посев 1/24", range: `${Math.round(avgViews * 0.7 / 1000)}К–${Math.round(avgViews * 1.0 / 1000)}К` },
    { format: "Посев 2/48", range: `${Math.round(avgViews * 1.0 / 1000)}К–${Math.round(avgViews * 1.4 / 1000)}К` },
    { format: "Клип",       range: `${Math.round(avgViews * 1.3 / 1000)}К–${Math.round(avgViews * 2.0 / 1000)}К` },
    { format: "Сторис",     range: `${Math.round(avgViews * 0.5 / 1000)}К–${Math.round(avgViews * 0.8 / 1000)}К` },
  ] : [
    { format: "Шортс", range: `${Math.round(avgViews * 1.5 / 1000)}К–${Math.round(avgViews * 3.0 / 1000)}К` },
    { format: "Видео", range: `${Math.round(avgViews * 0.8 / 1000)}К–${Math.round(avgViews * 1.5 / 1000)}К` },
  ];

  const adsPerMonth = 2 + (h("am") % 8);
  const reach24h = isTg ? 65 + (h("r24") % 15) : isVk ? 52 + (h("r24") % 16) : 40 + (h("r24") % 20);
  const reach48h = Math.min(95, reach24h + 12 + (h("r48") % 10));
  const reach72h = Math.min(98, reach48h + 5 + (h("r72") % 8));

  const liveAudiencePct = liveAudience;
  const suspiciousPct = 4 + (h("sp") % 10);
  const botsPct = 2 + (h("bp") % 6);
  const organicGrowth = (h("og") % 5 === 0) ? "warn" : "ok";
  const massFollowing = (h("mf") % 7 === 0) ? "warn" : "ok";
  const viewsManipulation = (h("vm") % 8 === 0) ? "warn" : "ok";
  const reactionsManipulation = (h("rm") % 9 === 0) ? "warn" : "ok";

  return {
    key, label: PLATFORM_LABELS[key] ?? key,
    avgViews, er, cpv, liveAudience, postsPerMonth,
    viewsDeltaPct, erVsMedian, livePercentile,
    malePct, ageGroups, geo, interests, sparkline,
    formatReach, adsPerMonth, reach24h, reach48h, reach72h,
    liveAudiencePct, suspiciousPct, botsPct,
    organicGrowth, massFollowing, viewsManipulation, reactionsManipulation,
  };
}

export function generateProfile(blogger: Blogger): BloggerProfileData {
  const h = (s: string) => mh(blogger.id + s);
  const primary = normalizePlatform(blogger.platform) as PlatformKey;

  // Determine secondary platforms
  const platforms: PlatformKey[] = [primary];
  if (primary === "tg" && h("p2") % 3 === 0) platforms.push("vk");
  if (primary === "tg" && h("p3") % 5 === 0) platforms.push("yt");
  if (primary === "vk" && h("p2") % 3 === 0) platforms.push("tg");
  if (primary === "vk" && h("p3") % 6 === 0) platforms.push("dzen");

  const platformStats: Partial<Record<PlatformKey, PlatformProfile>> = {};
  for (const key of platforms) {
    const scale = key === primary ? 1 : (key === "vk" ? 0.65 : key === "yt" ? 0.45 : 0.55);
    platformStats[key] = makePlatformProfile(key, blogger.id, blogger.reach, blogger.er, blogger.cpv, scale);
  }

  const qualityScore = 62 + (h("qs") % 35);
  const geoLabel = GEOS[h("geo") % GEOS.length];
  const yearsOnPlatform = 1 + (h("yp") % 8);
  const responseHours = 1 + (h("rh") % 12);
  const isVerified = h("vr") % 3 !== 0;
  const completedCampaigns = 2 + (h("cc") % 28);

  const posts: PostData[] = Array.from({ length: 9 }, (_, i) => {
    const types: PostData["type"][] = ["photo", "text", "video"];
    const daysAgo = (i + 1) * 2 + (h("pd" + i) % 3);
    const d = new Date(Date.now() - daysAgo * 86400000);
    return {
      id: `${blogger.id}-post-${i}`,
      type: types[h("pt" + i) % 3],
      excerpt: POST_EXCERPTS[h("pe" + i) % POST_EXCERPTS.length],
      views: Math.round(blogger.reach * (0.6 + (h("pv" + i) % 80) / 100)),
      date: d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" }),
    };
  });

  const history: HistoryEntry[] = Array.from({ length: 3 + (h("hc") % 4) }, (_, i) => {
    const brand = BRANDS[h("hb" + i) % BRANDS.length];
    const fmts = ["Посев · 1/24", "Натив · Пост", "Посев · 2/48", "Натив · Кружочек", "Клип"];
    const months = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
    const year = 2024 + (i === 0 ? 1 : 0);
    const month = months[(h("hm" + i) + i * 2) % 12];
    return {
      brand,
      formatLabel: fmts[h("hf" + i) % fmts.length],
      date: `${month} ${year}`,
      result: (h("hr" + i) % 3 === 0) ? "great" : "ok",
      deltaLabel: (h("hr" + i) % 3 === 0)
        ? `+${8 + (h("hdelta" + i) % 15)}% к плану`
        : "в срок",
    };
  });

  const reviews: ReviewEntry[] = Array.from({ length: 2 + (h("rc") % 4) }, (_, i) => {
    const a = AUTHORS[h("ra" + i) % AUTHORS.length];
    const daysAgo = 10 + i * 20 + (h("rd" + i) % 30);
    const d = new Date(Date.now() - daysAgo * 86400000);
    const stars = 4 + (h("rs" + i) % 2 === 0 ? 1 : 0);
    return {
      id: `${blogger.id}-rev-${i}`,
      author: a.name, company: a.company,
      date: d.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" }),
      stars,
      text: REVIEW_TEXTS[h("rt" + i) % REVIEW_TEXTS.length],
      format: ["Посев", "Натив", "Кружочек"][h("rf" + i) % 3],
      segment: ["B2C", "B2B", "E-commerce"][h("rseg" + i) % 3],
    };
  });

  const similar: SimilarBlogger[] = Array.from({ length: 3 }, (_, i) => {
    const sid = `sim-${blogger.id}-${i}`;
    return {
      id: sid,
      name: [`@${blogger.name}_alt`, "MediaPro", "FinanceChannel"][i],
      platform: primary,
      category: blogger.cats[0] ?? "Разное",
      cpv: +(blogger.cpv * (0.8 + (mh(sid) % 40) / 100)).toFixed(2),
      er: +(blogger.er * (0.7 + (mh(sid + "e") % 60) / 100)).toFixed(1),
    };
  });

  return {
    platforms, qualityScore, geoLabel,
    yearsOnPlatform, responseHours, isVerified, completedCampaigns,
    platformStats, posts, history, reviews, similar,
  };
}

export { PLATFORM_LABELS };
